/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect } from "react";

import {
  useUnityDispatch,
  useUnitySelector,
  UnityAppStore
} from "../../viewModel";
import {
  closeFeatureOverride,
  cycleFeatureOverride,
  focusNextControl,
  focusPrevControl,
  resetAllFeatureOverrides,
  resetAllStageOverrides,
  selectFeatureOverrideFocusedIndex,
  selectIsFeatureOverrideVisible,
  selectResetFeatureOverridesIndex,
  selectResetStageOverridesIndex,
  selectVideoDebugOverlayControlIndex
} from "../../viewModel/featureOverride";
import {
  selectIsVideoDebugOverlayPersisted,
  setVideoDebugOverlayEnabled
} from "../../viewModel/videoDebugOverlay";
import { InputAction } from "../../viewModel/navigation";
import type {
  InputInterceptor,
  NavigationEngine
} from "../../viewModel/navigation";

/** Name used to register/remove the interceptor on the engine. */
const INTERCEPTOR_NAME: string = "featureOverrideInput";

/**
 * Captures D-pad input for the debug Feature Override screen while it is open.
 *
 * Unlike a `ModalController`, which is only reached when the active screen has
 * a registered controller, this interceptor sits at the top of the input
 * pipeline (the same place the `123456` trigger lives), so the overlay responds
 * to the remote no matter what is behind it — including the player, splash or
 * any screen that registers no controller.
 *
 * UP/DOWN walks the flat control list — two controls per feature (Stage, then
 * Activation Override), the two reset buttons, then the Video Debug Overlay
 * switch — and LEFT/RIGHT cycles whichever control has focus, so the same two
 * keys drive every kind of configuration on the screen. OK activates the
 * trailing controls. See
 * `viewModel/featureOverride/featureOverrideControls.ts`.
 *
 * Installed only while the overlay is visible; every key event is consumed so
 * nothing leaks to the screen underneath, and keyups are swallowed so a
 * background screen never sees the release of a key whose press was handled
 * here. Pointer/scroll events pass through so the on-screen buttons still work.
 *
 * @param engine - The NavigationEngine instance from context.
 */
export function useFeatureOverrideInput(engine: NavigationEngine): void {
  const dispatch = useUnityDispatch();
  const isVisible = useUnitySelector(selectIsFeatureOverrideVisible);

  useEffect((): (() => void) | undefined => {
    if (!isVisible) {
      return undefined;
    }

    /** Flips the persisted overlay preference, reading its current value
     * from the store rather than from this hook's props: the switch is a
     * plain on/off and both LEFT/RIGHT and OK act on it the same way. */
    const toggleVideoDebugOverlay = (): void => {
      const state = UnityAppStore.getState();
      dispatch(
        setVideoDebugOverlayEnabled(!selectIsVideoDebugOverlayPersisted(state))
      );
    };

    /**
     * LEFT/RIGHT on the focused control.
     *
     * The per-feature controls cycle through a list of choices, which is
     * the slice's own business. The trailing overlay switch belongs to a
     * different slice, so the composition happens here rather than giving
     * `featureOverride` a dependency on `videoDebugOverlay`.
     */
    const cycleFocusedControl = (direction: "next" | "prev"): void => {
      const state = UnityAppStore.getState();
      if (
        selectFeatureOverrideFocusedIndex(state) ===
        selectVideoDebugOverlayControlIndex(state)
      ) {
        toggleVideoDebugOverlay();
        return;
      }
      dispatch(cycleFeatureOverride(direction));
    };

    const interceptor: InputInterceptor = (event) => {
      if (event.eventType === "keyup") {
        return true;
      }
      if (event.eventType !== "keydown") {
        return undefined;
      }

      switch (event.action) {
        case InputAction.NAV_UP:
          dispatch(focusPrevControl());
          return true;
        case InputAction.NAV_DOWN:
          dispatch(focusNextControl());
          return true;
        case InputAction.NAV_LEFT:
          cycleFocusedControl("prev");
          return true;
        case InputAction.NAV_RIGHT:
          cycleFocusedControl("next");
          return true;
        case InputAction.NAV_OK: {
          const state = UnityAppStore.getState();
          const focused: number = selectFeatureOverrideFocusedIndex(state);
          if (focused === selectResetStageOverridesIndex(state)) {
            dispatch(resetAllStageOverrides());
          } else if (focused === selectResetFeatureOverridesIndex(state)) {
            dispatch(resetAllFeatureOverrides());
          } else if (focused === selectVideoDebugOverlayControlIndex(state)) {
            toggleVideoDebugOverlay();
          }
          return true;
        }
        case InputAction.BACK:
          dispatch(closeFeatureOverride());
          return true;
        default:
          // Block every other key from reaching the screen behind the overlay.
          return true;
      }
    };

    engine.getInterceptors().use(INTERCEPTOR_NAME, interceptor);

    return (): void => {
      engine.getInterceptors().remove(INTERCEPTOR_NAME);
    };
  }, [engine, isVisible, dispatch]);
}
